import { useEffect, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Outlet, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import Lenis from "lenis";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { captureUtm, trackRouteChange, injectAnalytics } from "@/lib/analytics";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import Home from "@/pages/Home";

const Catalogo = lazy(() => import("@/pages/Catalogo"));
const Servicos = lazy(() => import("@/pages/Servicos"));
const Orcamento = lazy(() => import(/* webpackChunkName: "orcamento-v3" */ "@/pages/Orcamento"));
const Duvidas = lazy(() => import("@/pages/Duvidas"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminReset = lazy(() => import("@/pages/admin/AdminLogin").then((m) => ({ default: m.AdminReset })));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));

function useLenis() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.1 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);
}

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RouteTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    const t = setTimeout(() => trackRouteChange(), 60);
    return () => clearTimeout(t);
  }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <Header />
      <main id="conteudo-principal">
        <Outlet />
      </main>
      <Footer />
      <WhatsAppFloat />
    </>
  );
}

function AdminNoIndex({ children }) {
  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy" data-testid="admin-loading">
        <span className="font-display text-sm font-bold uppercase tracking-[0.3em] text-white/60">Carregando…</span>
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppInner() {
  useLenis();
  useEffect(() => {
    captureUtm();
    try { localStorage.removeItem("bf_cookie_consent"); } catch (e) {}
    injectAnalytics();
  }, []);
  return (
    <BrowserRouter>
      <ScrollTop />
      <RouteTracker />
      <Suspense fallback={<div className="min-h-[60vh] bg-white" aria-hidden="true" />}>
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/catalogo-insulfilm-rj" element={<Catalogo />} />
            <Route path="/servicos" element={<Servicos />} />
            <Route path="/orcamento" element={<Orcamento />} />
            <Route path="/duvidas-sobre-insulfilm" element={<Duvidas />} />
            <Route path="/privacidade" element={<Navigate to="/" replace />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin/login" element={<AdminNoIndex><AdminLogin /></AdminNoIndex>} />
          <Route path="/admin/redefinir-senha" element={<AdminNoIndex><AdminReset /></AdminNoIndex>} />
          <Route path="/admin" element={<Protected><AdminNoIndex><AdminDashboard /></AdminNoIndex></Protected>} />
        </Routes>
      </Suspense>
      <div id="butterfly-layer" aria-hidden="true" />
      <Toaster position="top-center" richColors />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </HelmetProvider>
  );
}
